import { useState, useCallback, useEffect } from 'react';
import { SocialMediaAccountPublic, SocialPlatform } from '@/lib/api';
import { socialMediaApi } from '../api';
import { toast } from '@/components/ui/use-toast';

export function useSocialAccounts() {
  const [accounts, setAccounts] = useState<SocialMediaAccountPublic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.listSocialMediaAccounts();
      setAccounts(result.accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const connectAccount = useCallback(async (platform: SocialPlatform) => {
    try {
      const redirectUri = `${window.location.origin}/v1/arches/social-media/accounts/oauth/callback`;
      const result = await socialMediaApi.initSocialMediaOAuth(platform, redirectUri);
      window.location.href = result.authorizationUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start account connection';
      toast({ title: 'Connection failed', description: message, variant: 'destructive' });
    }
  }, []);

  const disconnectAccount = useCallback(async (accountId: string) => {
    try {
      await socialMediaApi.disconnectSocialMediaAccount(accountId);
      setAccounts((prev) => prev.filter((a) => a.accountId !== accountId));
      toast({ title: 'Account disconnected' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disconnect account';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  }, []);

  return { accounts, isLoading, error, loadAccounts, connectAccount, disconnectAccount };
}
